import { usePropertyField, usePropertyName } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Property-level 404 override. The platform page is not in pages.json and
 * this slot is not editor-editable — keep copy static and skip contentSchema.
 * Do not read window/document during render (empties SSG 404.html).
 */
export function NotFound() {
  const propertyName = usePropertyName();
  const featuredImage = usePropertyField('property_featured_image');

  return (
    <section className="bg-background">
      <div className="relative h-[30vh] min-h-[200px] max-h-[350px] overflow-hidden">
        {featuredImage ? (
          <VibesitesImage
            url={featuredImage}
            alt=""
            sizes="100vw"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-secondary" />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center text-primary-foreground">
          <p className="mb-3 font-body text-xs font-semibold uppercase tracking-[0.28em] text-accent">
            404
          </p>
          <h1 className="font-heading text-3xl font-normal uppercase tracking-wider sm:text-4xl md:text-5xl">
            Page Not Found
          </h1>
        </div>
      </div>

      <div className="bg-muted py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <div className="mx-auto mb-8 h-1 w-20 rounded-full notfound-accent-bar" />
          <p className="mx-auto max-w-xl font-body text-base leading-relaxed text-muted-foreground md:text-lg">
            {propertyName
              ? `This page does not exist or has moved. Let's get you back to ${propertyName}.`
              : 'This page does not exist or has moved. Head home to keep exploring.'}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="/"
              className="notfound-cta-primary group/home relative inline-flex min-w-[200px] items-center justify-center gap-2.5 overflow-hidden px-10 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300"
            >
              <span className="relative z-10">Back to Home</span>
              <svg
                className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover/home:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
            <a
              href="/floor-plans"
              className="inline-flex min-w-[200px] items-center justify-center border border-border px-10 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-foreground transition-all duration-300 hover:border-primary hover:text-primary"
            >
              View Floor Plans
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .notfound-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
        }
        .notfound-cta-primary {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .notfound-cta-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .notfound-cta-primary:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .notfound-cta-primary:hover::before {
          left: 100%;
        }
        .notfound-cta-primary:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
      `}</style>
    </section>
  );
}

export default NotFound;
